/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/ScheduleModern.tsx
 * PURPOSE: Modern schedule interface — employee's own calendar (read-only).
 *          2026-08-11: fixed BS-301 Route 3 AC #2 (registry SC-021 gap G1) —
 *          schedule-grid card title leaked the raw English `viewMode` enum
 *          ("Lịch làm việc - week") instead of a Vietnamese label.
 *          2026-08-15: web-sync checklist A.2 — added the shared
 *          EmployeeBottomNav (this route previously had no bottom nav at all).
 *          2026-08-17: BS-301 Route 3 §1 gaps — (1) FRESH vs EMPTY empty-state
 *          copy (useScheduleModern's hasAnyShiftEver) instead of one generic
 *          "Chưa có lịch làm việc" message for both; (2) "Thử lại" retry
 *          button on the error banner (previously error-only, no recovery
 *          action).
 *          2026-09-04: mobile-source-of-truth audit (SC-021-EMP-WEB-SCHEDULE,
 *          mirrors the SC-017-EMP-ATTENDANCE methodology) —
 *          (G2) built a REAL day/week/month grid mapped to actual calendar
 *          columns (mirrors mobile's WeekGrid + accordion / MonthView
 *          structure — `mobile/src/screens/schedule/ScheduleScreen.tsx`);
 *          previously every mode rendered the same flat responsive card list
 *          in array order with zero date-column mapping.
 *          (G3) already resolved by the 2026-08-17 empty-state rewrite (the
 *          mismatched "Tạo ca làm"/"+ Phân ca" empty-state button no longer
 *          exists on this page).
 *          (G4/G5) resolved by REMOVAL, not reconnection — see
 *          `useScheduleModern.ts` file header: mobile's employee schedule
 *          screen is READ-ONLY (no assignment/drag/compliance-override
 *          anywhere), so the dead labor-law `warningBox` (fed only by
 *          `assignStaff`, which this page never called) and the drag
 *          attributes with no `onDrop` (kept-but-inert manager-tool remnants)
 *          are gone, not wired up. Shift-assignment tooling for managers
 *          already exists separately at `/manager/schedule`
 *          (`ManagerSchedulePage.tsx`) — this route's RBAC guard
 *          (`ALL_TENANT_ROLES`, same tier as /attendance + /profile) confirms
 *          it IS the correct employee target, it just carried
 *          leftover manager-shaped dead code.
 *          2026-09-04 (screenshot-evidenced fix round, 3 issues found viewing
 *          `temp/issue-verify-2026-09-04/SC2-SCHEDULE-PASS-{week-grid,day-view}-1440.png`):
 *          (1) the week grid's "unconditional single-column stack" fix two
 *          entries above traded away the entire week-grid concept to dodge
 *          chip-text clipping — rebuilt it as a REAL always-horizontal 7-cell
 *          row (mirrors `mobile/src/components/ui/WeekGrid.tsx`: solid
 *          color-coded cells keyed by `weekDayMarker()` — coffee=hasShift,
 *          indigo=nightShift, muted=off — showing only the weekday+day-number
 *          and a SHORT shift code via `shortShiftLabel()`, +N badge for
 *          multi-shift days). Full shift detail (name/time/branch/position)
 *          stays OUT of the grid cell — it already lived in the
 *          click-to-expand `dayDetailPanel` below the grid, which this fix
 *          reuses unchanged. This is exactly why it fits at the 480px
 *          app-shell width without clipping: the cell content is minimal by
 *          design, not "smaller text" of the old full-detail chip.
 *          (2) Ngày (day) view's mostly-empty look: `.page` forces
 *          `min-height:100vh` (deliberate — matches the sibling
 *          AttendanceModern.module.css convention, keeps the gradient
 *          background full-bleed on short pages) but `.page` was never a
 *          flex container, so that height did nothing for `.main` — `.main`
 *          just sized to its own short content, leaving the remainder of the
 *          100vh box as bare gradient below it. Made `.page` a flex column +
 *          `.main` `flex:1`, so `.main`'s box now genuinely fills the
 *          available height; day mode additionally gets `.mainCentered`
 *          (`viewMode === 'day'` only, so week/month — already content-full —
 *          are untouched) to center its sparse content in that space instead
 *          of leaving it pinned at the top with dead space below.
 *          (3) day-view / day-detail-panel header line ("T6 · 2026-09-04")
 *          printed the raw ISO date next to an already-Vietnamese-formatted
 *          date-nav header above it — inconsistent. Added `fromLocalISO()` +
 *          `formatShortVN()` (parses the `YYYY-MM-DD` wire string via local
 *          Date components, same reasoning as the existing `toLocalISO()`
 *          note below — NOT `new Date(iso)`, which parses date-only strings
 *          as UTC and can shift the weekday near midnight) and reused the
 *          same `toLocaleDateString('vi-VN')` convention already used by
 *          `AttendanceModern.tsx` for its date strings.
 *          2026-09-04 (structural layout parity — read-only structural audit
 *          this session comparing this file + .module.css against mobile's
 *          ScheduleScreen.tsx line-by-line, same dimension already applied to
 *          the sibling AttendanceModern.tsx earlier this session): 1 root
 *          cause fixed — mobile's ViewToggle (week/month segmented pill,
 *          mobile/src/screens/schedule/components/ViewToggle.tsx) renders
 *          bare directly under the header, NOT Card-wrapped (mobile only
 *          Card-wraps the actual schedule content, ScheduleScreen.tsx:268);
 *          this page instead boxed the view-mode toggle + date-nav together
 *          inside a `.controlsCard` (background/shadow/padding "card"
 *          treatment) with no mobile analogue — same Card-wrapping-
 *          consistency root cause already fixed on AttendanceModern.tsx.
 *          Un-boxed to a bare `.controlsRow` (see inline comment above its
 *          JSX for why toggle + date-nav stay grouped together rather than
 *          splitting date-nav into `.scheduleCard`, unlike mobile's separate
 *          locations: `.scheduleCard` doesn't render in the empty/error
 *          states, so moving date-nav inside it would strand users unable to
 *          change dates from those states — a functional regression mobile
 *          doesn't have since its Card is always present in week mode).
 *          `.main` gained `display:flex;gap` (mirrors the convention already
 *          established on AttendanceModern.module.css) replacing the one-off
 *          `margin-bottom`/`margin-top` hacks on `.card`/`.controlsCard`/
 *          `.errorCard`. Reviewed-but-NOT-changed (confirmed intentional, not
 *          drift): the `.scheduleCard` inner title ("Lịch làm việc - {mode}")
 *          looks like it duplicates the page `<h1>Lịch làm việc</h1>` but is
 *          mandated by ratified BS-301 Route 3 AC #2 ("the schedule-grid card
 *          title must show the Vietnamese view-mode label") with an existing
 *          regression test (ScheduleModern.test.tsx) guarding it — not the
 *          same kind of accidental duplicate-header this session found and
 *          fixed on AttendanceModern (a static header + 4 screens' own
 *          duplicate in-body headers); this page has no per-state header (no
 *          screenState stack — day/week/month is a single screen), so there
 *          is no state-driven-header fix analogous to AttendanceModern's
 *          `headerConfig` to apply here. Also reviewed: week/month grid cell
 *          composition (taller columns with visible shift chips vs. mobile's
 *          compact 44px color-coded WeekGrid cells) — judged a deliberate,
 *          reasonable desktop-width adaptation from the prior G2 round, not
 *          unintentional drift to collapse back to mobile's mobile-only
 *          layout; no leftover manager/drag-only CSS classes found (grep
 *          confirmed every class in the .module.css is referenced in this
 *          file).
 *          2026-09-04 (regression fix, screenshot-confirmed — controls-jump
 *          bug): the day-view empty-space fix two entries above applied
 *          `.mainCentered` to the WHOLE `.main` container, which centers
 *          `.main`'s entire flex-column contents. But `.main` holds
 *          `.controlsRow` (Ngày/Tuần/Tháng toggle + date-nav) and the
 *          schedule content as SIBLINGS — centering `.main` as a whole
 *          dragged the controls down with the sparse day-view card instead
 *          of only repositioning the card. Fixed by scoping the centering to
 *          a new `.contentArea` wrapper around everything AFTER the controls
 *          row (scheduleCard/empty-state/error) — `.main` itself is never
 *          centered now, so `.controlsRow` renders at a fixed position
 *          identically in all 3 view modes; only `.contentArea` gets
 *          `justify-content:center` (via `.contentAreaCentered`), and only
 *          in Ngày mode. See `.module.css` `.contentArea` comment for the CSS
 *          side. (`.contentAreaCentered` đã được GỠ 2026-09-04 — xem mục kế.)
 *          2026-09-04 (Ngày view redesign, user-reported "layout khi chọn
 *          option ngày đang gây khó hiểu"): gốc rễ KHÔNG phải căn giữa mà là
 *          NỘI DUNG. Chế độ Ngày trước đây render đúng cùng một danh sách ca
 *          (`ShiftDetailList`) mà panel "bấm vào ô ngày" ở Tuần/Tháng đã hiện
 *          → chọn Ngày mà không thấy gì hơn, lại chiếm cả trang nên trông
 *          trống. Nay Ngày là TRANG CHI TIẾT MỘT NGÀY thật sự
 *          (`features/scheduleModern/DayDetailView.tsx`): tổng hợp ca/giờ,
 *          dòng thời gian 24h (thấy được ca gãy), khối chấm công THỰC TẾ của
 *          ngày đó (API lịch sử chấm công, khoảng ngày = đúng ngày đang xem)
 *          và hành động tiếp theo. Vì nội dung đã đầy, thủ thuật căn giữa
 *          `.contentAreaCentered` không còn cần và đã bị gỡ khỏi cả .tsx lẫn
 *          .module.css. Ngày cũng render NGOÀI điều kiện `hasData` để ngày
 *          nghỉ vẫn có nội dung thay vì rơi vào thẻ empty-state.
 *          Lưu ý chủ đích LỆCH mobile (mobile không có chế độ Ngày —
 *          `mobile/src/config/schedule.config.ts:221`): giữ theo quyết định
 *          của product owner 2026-09-04, lý do đầy đủ ghi trong
 *          DayDetailView.tsx.
 *          2026-09-04 (CLS fix — "buttons and components jump a lot,
 *          annoying on laggy networks", user-reported): the controls row
 *          (Ngày/Tuần/Tháng toggle + date-nav) was gated on `!loading`, so it
 *          was ABSENT from the DOM for the whole loading phase (initial
 *          mount, or any refetch) and popped into existence the instant
 *          loading finished — worse the longer the fetch takes, i.e. worse on
 *          exactly the laggy networks the user described. Un-gated the
 *          controls row to render unconditionally (matches mobile's
 *          ViewToggle, which is always-present with no loading-gated
 *          visibility) and moved the "Đang tải lịch làm việc..." placeholder
 *          into `.contentArea` (the schedule-content region) so only the
 *          content area shows a loading state while the controls row stays
 *          stable. Controls remain enabled during loading (not disabled) —
 *          switching view/date mid-fetch just triggers a fresh fetch, which
 *          is the existing behavior for any other state change; no mobile
 *          precedent or UX reason to block it. Verified with CDP network
 *          throttling that `.controlsRow`'s `getBoundingClientRect().top` is
 *          identical before/during/after the load, where previously it was
 *          `null` (absent) during load then appeared.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 3
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: OR-Tools integration with <60s schedule generation
 *
 * @business-context
 * - Domain: Page Routing, Vietnamese F&B Application Navigation
 * - Impact: User Experience, Application Flow, Navigation
 * - Stakeholders: End Users, Product Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript, Layout Components
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md
 * - Design: docs/02-design/04-ui-ux-design/
 * - Registry: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md `SC-021-EMP-WEB-SCHEDULE`
 * - Mobile parity source: mobile/src/screens/schedule/ScheduleScreen.tsx
 * - API: docs/03-integrate/01-API-Specifications/
 * - Tests: frontend/tests/pages/ScheduleModern.test.tsx
 *  */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DayDetailView } from '@/features/scheduleModern/DayDetailView';
import { useMyWeekProposal } from '@/features/scheduleProposal/useScheduleProposal';
import { useScheduleModern, ViewMode, Shift } from '@/features/scheduleModern/useScheduleModern';
import { EmployeeBottomNav } from '@/components/navigation/EmployeeBottomNav';
import styles from './ScheduleModern.module.css';

// BS-301 Route 3 AC #2 / registry SC-021 gap G1 (2026-08-11): the card title
// was printing the raw English `viewMode` enum ("Lịch làm việc - week") since
// the enum value was interpolated directly into Vietnamese UI copy.
const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  day: 'Ngày',
  week: 'Tuần',
  month: 'Tháng',
};

// Date.getDay() index (0=CN/Sun) — same table as mobile's
// `schedule.config.ts` VIETNAMESE_DAYS_SHORT.
const DAY_LABELS_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

// BOT-031 (GH#779): Monday-first header row for the MONTH-view grid only.
// `buildMonthGrid()` lays out columns starting Monday (via
// `getWeekStartMonday()`), but the header previously reused `DAY_LABELS_SHORT`
// verbatim (Sunday-first) — every date rendered under the wrong weekday
// column. Order matches the wireframe (persona-flows/02-employee/
// WIREFRAME-EMPLOYEE-FLOW.md FRAME 1 "T2 T3 T4 T5 T6 T7 CN") and the
// already-working Manager-side precedent (ManagerSchedulePage.tsx
// WEEKDAY_LABELS_VI). Don't reuse `DAY_LABELS_SHORT` here — it stays
// Sunday-indexed on purpose for the `date.getDay()` lookups elsewhere in
// this file.
const MONTH_GRID_HEADER_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

// ─────────────────────────────────────────────────────────────────────────
// Local-calendar date helpers (G2 grid layer, 2026-09-04)
// WHY local (not `toISOString()`): `shift.date` on the wire is a bare
// Postgres DATE (no time/TZ component). Building grid-cell keys via
// `toISOString()` converts through UTC and can shift the calendar day near
// midnight depending on the browser's local timezone — the exact class of
// bug already documented in backend `_vn_today()` (schedule.py). Grid cell
// keys are built from local Date fields instead so they line up with the
// literal `YYYY-MM-DD` strings the backend returns.
// ─────────────────────────────────────────────────────────────────────────
function toLocalISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Inverse of toLocalISO — parses a bare `YYYY-MM-DD` wire string via local
// Date components. Deliberately NOT `new Date(iso)`: that form parses
// date-only strings as UTC midnight, which can shift the weekday by one near
// local midnight — the same class of bug `toLocalISO`'s own note above warns
// against, just in the read direction.
function fromLocalISO(iso: string): Date {
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

// 2026-09-04 (day-view/day-detail date-format fix): the "T6 · 2026-09-04"
// header line printed the raw ISO string next to the already-Vietnamese
// date-nav header above it — reuse the same `toLocaleDateString('vi-VN')`
// convention AttendanceModern.tsx uses for its date strings instead.
function formatShortVN(iso: string): string {
  return fromLocalISO(iso).toLocaleDateString('vi-VN');
}

function getWeekStartMonday(d: Date): Date {
  const dow = d.getDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  return monday;
}

interface GridCell {
  date: Date;
  iso: string;
  shifts: Shift[];
}

function buildWeekDays(selectedDate: Date, shiftsByDate: Map<string, Shift[]>): GridCell[] {
  const monday = getWeekStartMonday(selectedDate);
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    const iso = toLocalISO(date);
    return { date, iso, shifts: shiftsByDate.get(iso) ?? [] };
  });
}

interface MonthCell extends GridCell {
  inMonth: boolean;
}

function buildMonthGrid(selectedDate: Date, shiftsByDate: Map<string, Shift[]>): MonthCell[] {
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const gridStart = getWeekStartMonday(new Date(year, month, 1));
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    const iso = toLocalISO(date);
    return { date, iso, inMonth: date.getMonth() === month, shifts: shiftsByDate.get(iso) ?? [] };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Week-grid cell styling (G2-fix round, 2026-09-04) — mirrors
// mobile/src/components/ui/WeekGrid.tsx's `markerFor`/`shiftCodeLabel`
// (mobile/src/utils/scheduleMappers.ts). Mobile derives night-shift from its
// shift-code time-period table; the web `Shift` doesn't carry that table, so
// this approximates via the legacy `shift_type` enum ('night', see
// useScheduleModern.ts SHIFT_TYPE_LABELS_LEGACY) or a plain start-hour check
// — same signal, simpler source.
// ─────────────────────────────────────────────────────────────────────────
type WeekDayMarker = 'hasShift' | 'nightShift' | 'off';

function isNightShift(s: Shift): boolean {
  if (s.shift_type === 'night') return true;
  const hour = parseInt(s.start_time.slice(0, 2), 10);
  if (Number.isNaN(hour)) return false;
  return hour >= 22 || hour < 5;
}

function weekDayMarker(shifts: Shift[]): WeekDayMarker {
  if (shifts.length === 0) return 'off';
  return shifts.some(isNightShift) ? 'nightShift' : 'hasShift';
}

// Compact 1-4 char cell label — the grid cell is deliberately glanceable
// (full name/time/branch lives in the click-to-expand detail panel below),
// mirrors mobile's short shift codes (S/C/T…). Prefers the real
// `shift_code` (M1/A2/N7…) when present; falls back to the first letter of
// the (already-Vietnamese-translated) title's last word ("Ca sáng" → "S").
function shortShiftLabel(s: Shift): string {
  if (s.shift_code) return s.shift_code.slice(0, 4);
  const words = s.title.trim().split(/\s+/);
  const last = words[words.length - 1] || s.title;
  return (last.charAt(0) || '?').toUpperCase();
}

// Shared shift-detail list — used by the week/month "chọn ngày để xem chi
// tiết" panel (mirrors mobile's WeekGrid accordion) and by the Ngày view.
const ShiftDetailList: React.FC<{ shifts: Shift[] }> = ({ shifts }) => {
  if (shifts.length === 0) {
    return <p className={styles.dayNoShiftDetail}>Không có ca ngày này.</p>;
  }
  return (
    <>
      {shifts.map(s => (
        <div key={s.id} className={styles.dayDetailShift}>
          <div className={styles.dayDetailShiftTitle}>
            {s.shift_code ? `Mã ca ${s.shift_code} · ` : ''}
            {s.title} · {s.start_time}–{s.end_time}
          </div>
          {(s.branch_name || s.position) && (
            <div className={styles.dayDetailShiftMeta}>
              {s.branch_name ? `Chi nhánh: ${s.branch_name}` : ''}
              {s.branch_name && s.position ? ' · ' : ''}
              {s.position ? `Vị trí: ${s.position}` : ''}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export const ScheduleModern: React.FC = () => {
  const navigate = useNavigate();
  const {
    viewMode,
    selectedDate,
    shifts,
    loading,
    error,
    hasAnyShiftEver,
    setViewMode,
    setSelectedDate,
    loadShifts,
  } = useScheduleModern();

  // Week/Month grid: which day's detail panel is expanded (mirrors mobile's
  // `selectedDayIso` accordion state in ScheduleScreen.tsx).
  const [selectedDayIso, setSelectedDayIso] = useState<string | null>(null);
  const toggleDay = (iso: string) => setSelectedDayIso(prev => (prev === iso ? null : iso));

  const hasData = shifts.length > 0;

  const shiftsByDate = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) {
      const list = map.get(s.date);
      if (list) list.push(s);
      else map.set(s.date, [s]);
    }
    return map;
  }, [shifts]);

  const weekDays = useMemo(
    () => (viewMode === 'week' ? buildWeekDays(selectedDate, shiftsByDate) : []),
    [viewMode, selectedDate, shiftsByDate]
  );
  const monthCells = useMemo(
    () => (viewMode === 'month' ? buildMonthGrid(selectedDate, shiftsByDate) : []),
    [viewMode, selectedDate, shiftsByDate]
  );
  const dayIso = useMemo(() => toLocalISO(selectedDate), [selectedDate]);

  // Lối vào "Đăng ký ca" — bám đúng bản mobile (ScheduleScreen.tsx: thẻ cuối
  // màn, phụ đề đổi theo số ca đang chờ duyệt / bị từ chối của TUẦN đang xem).
  const weekStartIso = useMemo(() => toLocalISO(getWeekStartMonday(selectedDate)), [selectedDate]);
  const proposalQuery = useMyWeekProposal(weekStartIso);
  const proposalCounts = useMemo(() => {
    const items = proposalQuery.data?.items ?? [];
    return {
      pending: items.filter(i => i.approval_status === 'PENDING').length,
      rejected: items.filter(i => i.approval_status === 'REJECTED').length,
    };
  }, [proposalQuery.data]);
  const todayIso = useMemo(() => toLocalISO(new Date()), []);

  // Helper for view mode button class
  const getViewModeClass = (mode: ViewMode) =>
    viewMode === mode
      ? `${styles.viewModeButton} ${styles.viewModeButtonActive}`
      : styles.viewModeButton;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 id="schedule-modern-title" data-testid="schedule-page-title" className={styles.title}>Lịch làm việc</h1>
          <button
            onClick={() => navigate('/dashboard/staff')}
            className={styles.backButton}
          >
            Về Dashboard
          </button>
        </div>
      </header>

      {/* 2026-09-04 (regression fix — controls-jump bug): the previous round
          applied `.mainCentered` to `.main` itself, which vertically centers
          `.main`'s ENTIRE flex-column — including `.controlsRow` (the
          Ngày/Tuần/Tháng toggle + date-nav), a sibling of the sparse day-view
          content it was meant to reposition. That dragged the controls down
          the page whenever Ngày mode was selected (screenshot-confirmed:
          ~130px from top in Tuần/Tháng vs ~300px+ in Ngày). `.main` itself is
          never centered now — it stays a plain flex-column so the controls
          render at a fixed position in ALL 3 modes. Only the NEW
          `.contentArea` wrapper below (holding scheduleCard/empty/error,
          i.e. everything AFTER the controls) was the only thing centered.
          2026-09-04: việc căn giữa đó đã GỠ hẳn — Ngày nay có nội dung đầy
          (DayDetailView), không còn gì cần căn giữa. */}
      <main
        aria-labelledby="schedule-modern-title"
        className={styles.main}
      >
        {/* View controls — structural layout parity (2026-09-04): mobile's
            ViewToggle (mobile/src/screens/schedule/components/ViewToggle.tsx)
            is a bare pill control directly below the header, NOT wrapped in a
            Card (mobile only Card-wraps the actual schedule content — the
            "Lịch của bạn" week card at ScheduleScreen.tsx:268). This block
            previously boxed the view-mode toggle + date nav inside a
            `.controlsCard` (background/shadow/padding "card" treatment) that
            has no mobile analogue — same Card-wrapping-consistency root cause
            already fixed on the sibling AttendanceModern.tsx this session.
            Un-boxed to a single bare `.controlsRow`; toggle + date-nav stay
            grouped together (unlike mobile's separate ViewToggle/WeekNavigator
            locations) because both must stay visible even when the schedule
            card itself doesn't render (empty/error states) — moving date-nav
            inside `.scheduleCard` would strand users unable to change dates
            from those states.
            2026-09-04 (CLS fix — controls-row disappear/pop bug): this block
            was previously gated on `!loading`, so it was ABSENT from the DOM
            for the entire duration of every load (initial mount, or any
            refetch that re-sets `loading=true`) and then popped into
            existence the instant loading finished — a large, jarring layout
            shift, worse the longer the fetch takes (i.e. worse on laggy
            networks, which is exactly when users notice it most). Mobile's
            ViewToggle has no loading-gated visibility at all — it's a bare,
            always-present pill directly under the header. Un-gated to match:
            the controls row now renders unconditionally so it occupies a
            stable position in the DOM from the very first paint. The loading
            placeholder moved into `.contentArea` below (see that block) so
            only the schedule CONTENT shows a loading state, not the controls.
            Controls stay enabled during loading rather than disabled: picking
            a different view mode or date while a fetch is in flight should
            just kick off a fresh fetch, not be blocked — there's no mobile
            precedent for disabling ViewToggle during load, and blocking input
            here would be an unforced UX regression, not a CLS fix. */}
        <div className={styles.controlsRow}>
          {/* View mode selector */}
          <div className={styles.viewModeGroup}>
            {(['day', 'week', 'month'] as ViewMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => { setSelectedDayIso(null); setViewMode(mode); }}
                className={getViewModeClass(mode)}
              >
                {mode === 'day' ? 'Ngày' : mode === 'week' ? 'Tuần' : 'Tháng'}
              </button>
            ))}
          </div>

          {/* Date navigation */}
          <div className={styles.dateNavGroup}>
            <button
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() - (viewMode === 'day' ? 1 : viewMode === 'week' ? 7 : 30));
                setSelectedDayIso(null);
                setSelectedDate(newDate);
              }}
              className={styles.navButton}
            >
              ←
            </button>
            {/* 2026-09-04 (báo cáo live): bộ ←/→ của Tháng bị rớt XUỐNG HÀNG
                DƯỚI trong khi Tuần nằm cùng hàng với 3 nút chế độ. Nguyên nhân
                KHÔNG phải layout mà là ĐỘ DÀI NHÃN: `month:'long'` cho ra
                "tháng 9 năm 2026" (~140px, vượt `.dateLabel{min-width:120px}`)
                nên `.controlsRow{flex-wrap:wrap}` đẩy cả nhóm xuống hàng mới;
                Tuần dùng dạng số "04/09/2026" (~95px) nên vừa. Ngày cũng dính
                cùng lỗi với `weekday:'long'` ("Thứ Sáu, 04/09"). Đưa cả 2 về
                dạng gọn cùng phong cách số của Tuần → mọi chế độ vừa 1 hàng.
                ⚠️ LƯU Ý CÁCH DÙNG API (đã sập bẫy 1 lần, @tester bắt được):
                `month:'2-digit'` KHÔNG cho ra "09/2026" mà cho ra
                "tháng 09, 2026" (126px — vẫn tràn, hụt đúng 1px so với chỗ
                trống 448px) vì thiếu `day` thì vi-VN không chọn mẫu thuần số.
                Phải là `month:'numeric'` → "9/2026". Kiểm bằng node trước khi
                tin, đừng suy từ tên option.
                KHÔNG dùng `flex-wrap:nowrap` để "ép" — làm vậy sẽ tràn ngang ở
                màn hẹp thật thay vì rớt hàng, đổi lỗi này lấy lỗi khác. */}
            <span className={styles.dateLabel}>
              {selectedDate.toLocaleDateString('vi-VN', {
                ...(viewMode === 'month' ? { month: 'numeric', year: 'numeric' } :
                    viewMode === 'week' ? { day: '2-digit', month: '2-digit', year: 'numeric' } :
                    { weekday: 'short', day: '2-digit', month: '2-digit' })
              })}
            </span>
            <button
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() + (viewMode === 'day' ? 1 : viewMode === 'week' ? 7 : 30));
                setSelectedDayIso(null);
                setSelectedDate(newDate);
              }}
              className={styles.navButton}
            >
              →
            </button>
          </div>
        </div>

        {/* Content area (2026-09-04, controls-jump regression fix): wraps
            everything that renders BELOW the controls row (schedule
            card/grid, empty states, error) — NOT the controls row itself,
            which must stay pinned at the top of `.main` in every view mode.
            (2026-09-04: `.contentAreaCentered` — thủ thuật căn giữa nội dung
            Ngày thưa thớt — đã gỡ; Ngày giờ có nội dung đầy nên wrapper này
            chỉ còn vai trò tách nội dung khỏi controls row.)
            2026-09-04 (CLS fix): the loading placeholder now lives HERE
            (was a `.main`-level sibling of the controls row, gating the
            controls' own presence — see the controls-row comment above) so
            only the schedule content area shows a loading state; the
            controls row above is unconditional and stable throughout. */}
        <div className={styles.contentArea}>
        {/* Loading state */}
        {loading && (
          <div role="status" aria-live="polite" className={styles.card}>
            <h2 className={styles.cardTitle}>Đang tải lịch làm việc...</h2>
            <p className={styles.cardDescription}>
              Vui lòng chờ trong giây lát. Hệ thống đang tải dữ liệu lịch làm việc.
            </p>
          </div>
        )}

        {/* Schedule grid — real day/week/month columns (G2, 2026-09-04) */}
        {!loading && hasData && viewMode !== 'day' && (
          <div className={styles.scheduleCard}>
            <h3 className={styles.scheduleTitle}>Lịch làm việc - {VIEW_MODE_LABELS[viewMode]}</h3>

            {viewMode === 'week' && (
              <>
                {/* Real always-horizontal 7-cell row (G2-fix round, 2026-09-04
                    — see file header). Mirrors mobile's WeekGrid: each cell
                    is minimal/glanceable (weekday+day-number + a short shift
                    code, solid color-coded by `weekDayMarker`), NOT a place
                    to cram full shift name/time — that lives in the
                    click-to-expand `dayDetailPanel` below, unchanged. */}
                <div className={styles.weekGrid} data-testid="schedule-week-grid">
                  {weekDays.map(day => {
                    const marker = weekDayMarker(day.shifts);
                    const primaryShift = day.shifts[0];
                    const extraCount = day.shifts.length > 1 ? day.shifts.length - 1 : 0;
                    return (
                      <div
                        key={day.iso}
                        className={[
                          styles.weekDayColumn,
                          marker === 'hasShift' ? styles.weekDayColumnHasShift :
                            marker === 'nightShift' ? styles.weekDayColumnNight :
                            styles.weekDayColumnOff,
                          day.iso === todayIso ? styles.weekDayColumnToday : '',
                          day.iso === selectedDayIso ? styles.weekDayColumnSelected : '',
                        ].filter(Boolean).join(' ')}
                        data-testid={`schedule-week-day-${day.iso}`}
                      >
                        <button
                          type="button"
                          className={styles.weekDayButton}
                          onClick={() => toggleDay(day.iso)}
                        >
                          <span className={styles.weekDayLabel}>
                            {DAY_LABELS_SHORT[day.date.getDay()]} {day.date.getDate()}
                          </span>
                          <span className={styles.weekDayShiftRow}>
                            <span className={styles.weekDayShiftCode}>
                              {primaryShift ? shortShiftLabel(primaryShift) : 'Nghỉ'}
                            </span>
                            {extraCount > 0 && (
                              <span className={styles.weekDayExtraBadge}>+{extraCount}</span>
                            )}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {selectedDayIso && (
                  <div className={styles.dayDetailPanel} data-testid="schedule-day-detail">
                    <div className={styles.dayDetailHeader}>
                      <span>{DAY_LABELS_SHORT[fromLocalISO(selectedDayIso).getDay()]} · {formatShortVN(selectedDayIso)}</span>
                      <button
                        type="button"
                        className={styles.dayDetailClose}
                        onClick={() => setSelectedDayIso(null)}
                      >
                        ✕ Thu gọn
                      </button>
                    </div>
                    <ShiftDetailList shifts={shiftsByDate.get(selectedDayIso) ?? []} />
                  </div>
                )}
              </>
            )}

            {viewMode === 'month' && (
              <>
                <div className={styles.monthGrid} data-testid="schedule-month-grid">
                  {MONTH_GRID_HEADER_LABELS.map(label => (
                    <div key={label} className={styles.monthGridHeaderCell}>{label}</div>
                  ))}
                  {monthCells.map(cell => (
                    <button
                      type="button"
                      key={cell.iso}
                      onClick={() => toggleDay(cell.iso)}
                      data-testid={`schedule-month-day-${cell.iso}`}
                      className={[
                        styles.monthDayCell,
                        cell.inMonth ? '' : styles.monthDayCellOutside,
                        cell.iso === todayIso ? styles.monthDayCellToday : '',
                        cell.iso === selectedDayIso ? styles.monthDayCellSelected : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <span className={styles.monthDayNumber}>{cell.date.getDate()}</span>
                      {cell.shifts.length > 0 && (
                        <span className={styles.monthDayIndicator} data-testid={`schedule-month-day-${cell.iso}-indicator`} />
                      )}
                    </button>
                  ))}
                </div>
                {selectedDayIso && (
                  <div className={styles.dayDetailPanel} data-testid="schedule-day-detail">
                    <div className={styles.dayDetailHeader}>
                      <span>{DAY_LABELS_SHORT[fromLocalISO(selectedDayIso).getDay()]} · {formatShortVN(selectedDayIso)}</span>
                      <button
                        type="button"
                        className={styles.dayDetailClose}
                        onClick={() => setSelectedDayIso(null)}
                      >
                        ✕ Thu gọn
                      </button>
                    </div>
                    <ShiftDetailList shifts={shiftsByDate.get(selectedDayIso) ?? []} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Chế độ NGÀY (2026-09-04) — KHÔNG dùng chung `.scheduleCard` grid ở
            trên: Ngày giờ là "trang chi tiết một ngày" đầy đủ (tổng hợp + dòng
            thời gian + chấm công thực tế + hành động), và phải hiện CẢ khi
            ngày đó không có ca (ngày nghỉ vẫn cần thấy tiêu đề ngày, khối chấm
            công và lối đi tiếp) — nên nó nằm NGOÀI điều kiện `hasData`, và hai
            thẻ empty-state phía dưới được loại trừ ở chế độ này để không hiện
            hai lần cùng một thông điệp "không có ca". Xem @cross-references +
            ghi chú lệch-mobile trong DayDetailView.tsx. */}
        {!loading && viewMode === 'day' && (
          <div className={styles.scheduleCard}>
            <DayDetailView
              dayIso={dayIso}
              todayIso={todayIso}
              shifts={shiftsByDate.get(dayIso) ?? []}
            />
          </div>
        )}

        {/* Empty state — BS-301 Route 3 §1 (2026-08-17): FRESH ("chưa từng có
            lịch") vs EMPTY ("tháng đang xem chưa xếp lịch") read differently
            per Gherkin; hasAnyShiftEver===false is the only case that means
            "never had ANY shift" (null = probe still pending/failed, treated
            as the safer EMPTY-this-period copy rather than asserting FRESH). */}
        {!loading && !hasData && viewMode !== 'day' && hasAnyShiftEver === false && (
          <div className={styles.card} data-testid="schedule-empty-fresh">
            <h2 className={styles.cardTitle}>Bạn chưa được phân ca lần nào</h2>
            <p className={styles.cardDescription}>
              Quản lý sẽ phân ca cho bạn; lịch làm việc sẽ xuất hiện tại đây khi được phân công.
            </p>
          </div>
        )}
        {!loading && !hasData && viewMode !== 'day' && hasAnyShiftEver !== false && (
          <div className={styles.card} data-testid="schedule-empty-period">
            <h2 className={styles.cardTitle}>
              {viewMode === 'month'
                ? `Tháng ${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()} chưa có lịch ca`
                : 'Khoảng thời gian này chưa có lịch ca'}
            </h2>
            <p className={styles.cardDescription}>
              Bạn có lịch làm việc ở các tháng khác. Hãy quay lại tháng hiện tại để xem lịch gần nhất.
            </p>
            <button
              type="button"
              onClick={() => setSelectedDate(new Date())}
              className={styles.navButton}
              data-testid="schedule-back-to-today"
            >
              Quay lại hôm nay
            </button>
          </div>
        )}

        {/* Lối vào "Đăng ký ca làm việc" (2026-09-04) — trước đây luồng này chỉ
            có ở mobile. Đặt cuối nội dung, đúng vị trí bản mobile. */}
        <button
          type="button"
          className={styles.proposalEntry}
          onClick={() => navigate('/schedule/register')}
          data-testid="schedule-proposal-entry"
        >
          <span className={styles.proposalEntryLabel}>Đăng ký ca</span>
          <span className={styles.proposalEntrySubtitle}>
            {proposalCounts.pending > 0
              ? `${proposalCounts.pending} ca đang chờ quản lý duyệt`
              : proposalCounts.rejected > 0
                ? `${proposalCounts.rejected} ca bị từ chối — xem lý do`
                : 'Tự chọn ca cho tuần này, gửi quản lý duyệt.'}
          </span>
        </button>

        {/* Error display */}
        {error && (
          <div data-testid="error-banner" className={styles.errorCard}>
            <p className={styles.errorMessage}>
              {error}
            </p>
            <button
              type="button"
              onClick={() => loadShifts()}
              className={styles.navButton}
              data-testid="schedule-error-retry"
            >
              Thử lại
            </button>
          </div>
        )}
        </div>

      </main>

      {/* web-sync checklist A.2 (2026-08-15) — shared 5-tab bottom nav */}
      <EmployeeBottomNav />
    </div>
  );
};

export default ScheduleModern;
